/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';

import { deleteRemovePersonalization } from '@/api-sdk';
import {
  ConfigurationContextType,
  GlobalConfigurationContext,
  personalisation,
  PersonalisationType,
} from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';
import { PrintOptionMixin } from '../print-option-mixin';

import '@/components/personalisation/fg-personalisation';
import '@/components/personalisation/wholesale/fg-personalisation-status-bar';
import '../fg-configuration-print-option-summary';

@customElement('fg-configuration-print-option-small')
export class FgConfigurationPrintOptionSmall extends PrintOptionMixin(
  LitElement,
) {
  @consume({ context: GlobalConfigurationContext, subscribe: true })
  configurationContext!: ConfigurationContextType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext!: PersonalisationType[];

  render() {
    if (!this.printOption) {
      return null;
    }

    const {
      hasColorChoice,
      image,
      personalization,
      printPosition,
      description,
      code,
    } = this.printOption;

    const { type } = personalization || {};
    const { personalizable, configurationId, configurationData } =
      this.configurationContext;

    return html`
      <div
        class="print-option ${classMap({
          finished:
            personalisation.getDataWithCode(
              this.personalisationContext,
              code,
            ) !== undefined && type !== undefined,
        })}"
      >
        <div class="print-option-content-wrapper">
          ${image
            ? html`
                <img
                  src=${image}
                  alt=${printPosition.name}
                  width="72"
                  height="72"
                />
              `
            : nothing}

          <div class="print-option-details-wrapper">
            <strong>${printPosition.name}</strong>
            <div class="print-option-details">
              <p>${description}</p>
            </div>
          </div>
        </div>
        ${!personalizable && !personalization
          ? nothing
          : html`
              <fg-personalisation-status-bar
                part="wholesale"
                .showActions=${personalizable}
                .personalisationData=${personalizable
                  ? personalisation.getDataWithCode(
                      this.personalisationContext,
                      code,
                    )
                  : personalization
                    ? {
                        printOptionCode: code,
                        uploadedFileName: personalization.logo,
                        logoUuid: personalization.logoUuid,
                        type: personalization.type,
                        colors: personalization.colors,
                        textPersonalization:
                          personalization.textPersonalization,
                        visualDesignId: personalization.visualDesignId,
                      }
                    : undefined}
                @open=${this.openPersonalisationDialog}
                @remove=${() => {
                  personalisation.providerRemoveEvent(this, code);
                  deleteRemovePersonalization({
                    uuid: configurationId,
                    printOptionCode: code,
                  });
                }}
              ></fg-personalisation-status-bar>
            `}
      </div>

      <fg-personalisation
        ${ref(this.fgPersonalisationDialogRef)}
        .printOption=${this.printOption}
        .hasColorChoice=${hasColorChoice}
        .isVisualDesignerAvailable=${configurationData?.visualDesigner
          ?.isVisualDesignerAvailable || false}
        @personalisation-completed=${this.savePersonalisationData}
      >
        <fg-configuration-print-option-summary
          slot="print-option-summary"
          .option=${this.printOption}
        ></fg-configuration-print-option-summary>
      </fg-personalisation>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .print-option {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        min-height: 88px;
        padding: 16px;
        gap: 8px;
      }

      .print-option.finished {
        align-items: flex-start;
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
        border: 1px solid var(--fg-success, var(--fg-fallback-success));
      }

      .print-option img {
        aspect-ratio: 64 / 64;
        flex-shrink: 0;
        margin-right: 16px;
        object-fit: contain;
      }

      .print-option button {
        width: 100%;
      }

      .print-option button {
        margin-top: 16px;
      }

      fg-personalisation-status-bar {
        width: 100%;
      }

      .print-option-details {
        display: flex;
        flex-direction: column;
        font-size: 16px;
        gap: 1px;
      }

      .print-option-details p {
        display: flex;
        flex-wrap: wrap;
        font-size: 0.875rem;
      }

      .print-option-details span.divider {
        margin-right: 2px;
      }

      strong {
        font-size: 1rem;
      }

      .print-option-content-wrapper {
        align-items: center;
        display: flex;
      }

      .print-option-details-wrapper {
        display: flex;
        flex-direction: column;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .print-option {
          padding: 12px 16px;
        }

        .print-option button {
          margin-top: 0;
          width: auto;
        }

        .print-option-details p {
          font-size: 1rem;
        }

        .print-option-details-wrapper {
          padding-block: 8px;
        }
      }
    `,
  ];
}
